const ORG_ID = process.env.REACT_APP_GITBOOK_ORG_ID;
const SITE_ID = process.env.REACT_APP_GITBOOK_SITE_ID;
const TOKEN = process.env.REACT_APP_GITBOOK_TOKEN;

const GCI_VCI_CONTEXT = 
  "Do not mix GCI/gene curation guidance with VCI/variant curation guidance; answer from the relevant documentation context only. Do not add links to the sources within the answer itself";

async function readGitbookStream(response, onChunk) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalParsed = null;
  let done = false;

  do {
    const { value, done: isDone } = await reader.read();
    done = isDone;

    if (value) {
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith('data:')) continue;

        const jsonString = trimmed.replace(/^data:\s*/, '');
        if (jsonString.toLowerCase() === 'done') continue;

        try {
          const parsed = JSON.parse(jsonString);
          if (onChunk) onChunk(parsed);
          finalParsed = parsed;
        } catch (err) {
          console.error("Error parsing streamed data:", err, jsonString);
        }
      }
    }
  } while (!done);

  if (buffer.trim()) {
    try {
      const parsed = JSON.parse(buffer.replace(/^data:\s*/, ''));
      if (onChunk) onChunk(parsed);
      finalParsed = parsed;
    } catch (err) {
      console.error("Error parsing final SSE chunk:", err, buffer);
    }
  }

  return finalParsed;
}

export async function askGitbookStream(question, onChunk, options = {}) {
  if (!ORG_ID || !SITE_ID || !TOKEN) throw new Error("GitBook credentials missing");

  const { signal } = options;
  const url = `https://api.gitbook.com/v1/orgs/${ORG_ID}/sites/${SITE_ID}/ask?format=markdown`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${TOKEN}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ 
      context: { goal:GCI_VCI_CONTEXT }, 
      question, 
      scope: { mode: "default" } 
    }),
    signal
  });

  if (!res.ok || !res.body) {
    const errorText = await res.text();
    throw new Error(`GitBook stream error: ${res.status} - ${errorText}`);
  }

  const finalData = await readGitbookStream(res, onChunk);

  // Enrich sources
  const enrichedSources = await Promise.all(
    finalData?.answer?.sources?.map(async (source) => {
      if (source.type === "page" && source.page && source.space && source.revision) {
        try {
          const [pageRes, spaceRes] = await Promise.all([
            fetch(`https://api.gitbook.com/v1/spaces/${source.space}/revisions/${source.revision}/page/${source.page}`, {
              headers: { Authorization: `Bearer ${TOKEN}` },
              signal
            }),
            fetch(`https://api.gitbook.com/v1/spaces/${source.space}`, {
              headers: { Authorization: `Bearer ${TOKEN}` },
              signal
            })
          ]);

          if (pageRes.ok && spaceRes.ok) {
            const pageData = await pageRes.json();
            const spaceData = await spaceRes.json();

            const publicBase = spaceData.urls?.public?.replace(/\/$/, '');
            const pagePath = pageData.path?.replace(/^\//, '');
            const fullUrl = publicBase && pagePath ? `${publicBase}/${pagePath}` : null;

            return {
              ...source,
              url: fullUrl,
              name: pageData.title || source.page
            };
          }
        } catch (err) {
          console.error("Error enriching source:", err);
        }
      }
      return source;
    }) || []
  );

  return {
    followupQuestions: finalData?.answer?.followupQuestions || [],
    sources: enrichedSources
  };
}

export async function getSuggestedQuestions() {
  if (!ORG_ID || !SITE_ID || !TOKEN) throw new Error("Missing GitBook credentials");

  const url = `https://api.gitbook.com/v1/orgs/${ORG_ID}/sites/${SITE_ID}/ask/questions`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${TOKEN}`
    }
  });

  if (!res.ok || !res.body) {
    const errText = await res.text();
    throw new Error(`GitBook questions stream error: ${res.status} - ${errText}`);
  }

  const questions = [];
  await readGitbookStream(res, (chunk) => {
    const question = chunk?.question;
    if (question) questions.push(question);
  });

  return questions;
}
